import { useState, useEffect, useMemo } from "react";
import {
    useCreateCampaign,
    useUpdateCampaign,
    type Campaign,
    type CampaignCreateRequest,
    type CampaignUpdateRequest,
} from "../api/campaigns";
import {
    useTeams,
    type Team,
    type TeamType,
    normalizeTeamTypeTier,
} from "../api/teams";
import { showError, showSuccess } from "../shared/toast";
import "./campaign-form.css";

const TEAM_SELECT_GROUPS: { types: TeamType[]; label: string }[] = [
    { types: [0], label: "Nationell" },
    { types: [1], label: "Distrikt" },
    { types: [2, 3], label: "Kretsar" },
];

export function groupTeamsForCampaignSelect(teamsList: Team[]) {
    const byType = new Map<TeamType, Team[]>();
    for (const t of teamsList) {
        const typ = normalizeTeamTypeTier(t.type);
        const list = byType.get(typ) ?? [];
        list.push(t);
        byType.set(typ, list);
    }
    for (const list of byType.values()) {
        list.sort((a, b) => a.name.localeCompare(b.name, "sv"));
    }
    return TEAM_SELECT_GROUPS.map((g) => ({
        label: g.label,
        teams: g.types
            .flatMap((typ) => byType.get(typ) ?? [])
            .sort((a, b) => a.name.localeCompare(b.name, "sv")),
    })).filter((g) => g.teams.length > 0);
}

/**
 * Serverfelen kommer antingen som ModelState (`{ "Name": ["..."] }`) eller från
 * JSON-bindningen (`{ "$.startDate": ["..."] }`). Nycklarna normaliseras till formulärets
 * fältnamn — annars hamnar felet i ett fält som aldrig renderas och sparaknappen ser ut att
 * inte göra någonting.
 */
const FORM_FIELDS = ["teamId", "name", "description", "startDate", "endDate"] as const;

export const mapServerErrors = (raw: Record<string, string[] | string>): Record<string, string> => {
    const mapped: Record<string, string> = {};
    const unmatched: string[] = [];

    for (const [key, value] of Object.entries(raw ?? {})) {
        const text = Array.isArray(value) ? value.join(" ") : String(value);
        const bare = key.replace(/^\$\./, "").split(".").pop() ?? key;
        const field = FORM_FIELDS.find((name) => name.toLowerCase() === bare.toLowerCase());

        if (field) {
            mapped[field] = text;
        } else {
            unmatched.push(text);
        }
    }

    if (unmatched.length > 0) {
        mapped.form = unmatched.join(" ");
    }

    return mapped;
};

interface CampaignFormProps {
    campaign?: Campaign;
    onSuccess?: () => void;
    onCancel?: () => void;
}

export const CampaignForm = ({
    campaign,
    onSuccess,
    onCancel,
}: CampaignFormProps): JSX.Element => {
    const isEditMode = !!campaign;
    const [formData, setFormData] = useState<CampaignCreateRequest>({
        teamId: campaign?.teamId || "",
        name: campaign?.name || "",
        description: campaign?.description || "",
        startDate: campaign?.startDate
            ? new Date(campaign.startDate).toISOString().split("T")[0]
            : "",
        endDate: campaign?.endDate
            ? new Date(campaign.endDate).toISOString().split("T")[0]
            : "",
    });
    const [errors, setErrors] = useState<Record<string, string>>({});

    const { data: teams = [], isLoading: isLoadingTeams } = useTeams();
    const teamSelectGroups = useMemo(() => groupTeamsForCampaignSelect(teams), [teams]);
    const createMutation = useCreateCampaign();
    const updateMutation = useUpdateCampaign();

    useEffect(() => {
        if (campaign) {
            setFormData({
                teamId: campaign.teamId,
                name: campaign.name,
                description: campaign.description || "",
                startDate: campaign.startDate
                    ? new Date(campaign.startDate).toISOString().split("T")[0]
                    : "",
                endDate: campaign.endDate
                    ? new Date(campaign.endDate).toISOString().split("T")[0]
                    : "",
            });
        }
    }, [campaign]);

    // Pre-select team when only one is available (create mode)
    useEffect(() => {
        if (!isEditMode && teams.length === 1 && !formData.teamId) {
            setFormData((prev) => ({ ...prev, teamId: teams[0].id }));
        }
    }, [isEditMode, teams, formData.teamId]);

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.teamId) {
            newErrors.teamId = "Team är obligatoriskt";
        }
        if (!formData.name.trim()) {
            newErrors.name = "Namn är obligatoriskt";
        }
        if (formData.name.length > 200) {
            newErrors.name = "Namn får max vara 200 tecken";
        }
        if (formData.description && formData.description.length > 1000) {
            newErrors.description = "Beskrivning får max vara 1000 tecken";
        }
        if (formData.startDate && formData.endDate) {
            const start = new Date(formData.startDate);
            const end = new Date(formData.endDate);
            if (end <= start) {
                newErrors.endDate = "Slutdatum måste vara efter startdatum";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        try {
            if (isEditMode && campaign) {
                const updateRequest: CampaignUpdateRequest = {
                    name: formData.name,
                    // Tom sträng nollställer beskrivningen. Skickas undefined ser API:t inget
                    // fält alls och den gamla texten ligger kvar.
                    description: formData.description ?? "",
                    startDate: formData.startDate
                        ? new Date(formData.startDate).toISOString()
                        : undefined,
                    endDate: formData.endDate
                        ? new Date(formData.endDate).toISOString()
                        : undefined,
                };
                await updateMutation.mutateAsync({
                    id: campaign.id,
                    ...updateRequest,
                });
            } else {
                // Tomma datum måste utelämnas — "" går inte att tolka som datum och gav
                // tidigare ett 400-svar som aldrig syntes i formuläret.
                await createMutation.mutateAsync({
                    teamId: formData.teamId,
                    name: formData.name.trim(),
                    description: formData.description?.trim() || undefined,
                    startDate: formData.startDate
                        ? new Date(formData.startDate).toISOString()
                        : undefined,
                    endDate: formData.endDate
                        ? new Date(formData.endDate).toISOString()
                        : undefined,
                });
            }
            onSuccess?.();
            if (isEditMode) {
                showSuccess("Kampanjen har uppdaterats");
            } else {
                showSuccess("Kampanjen har skapats");
            }
        } catch (error: any) {
            const data = error?.response?.data;
            const mapped = data?.errors ? mapServerErrors(data.errors) : {};
            const message =
                mapped.form ??
                data?.message ??
                data?.title ??
                (Object.values(mapped)[0] as string | undefined) ??
                "Ett fel uppstod";

            setErrors(Object.keys(mapped).length > 0 ? mapped : { form: message });
            // Alltid en toast också: felet kan höra till ett fält långt ned i formuläret.
            showError("Kunde inte spara kampanjen", message);
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const isLoading = createMutation.isPending || updateMutation.isPending;

    return (
        <form className="campaign-form" onSubmit={handleSubmit}>
            <h3>{isEditMode ? "Redigera kampanj" : "Skapa kampanj"}</h3>

            {errors.form && (
                <p role="alert" className="campaign-form__error">
                    {errors.form}
                </p>
            )}

            <div className="form-group">
                <label htmlFor="teamId">
                    Team <span aria-label="obligatoriskt">*</span>
                </label>
                <select
                    id="teamId"
                    name="teamId"
                    value={formData.teamId}
                    onChange={handleChange}
                    disabled={isEditMode || isLoadingTeams}
                    required
                    aria-required="true"
                    aria-invalid={!!errors.teamId}
                    aria-describedby={errors.teamId ? "teamId-error" : undefined}
                    className={errors.teamId ? "error-field" : ""}
                >
                    <option value="">
                        {isLoadingTeams ? "Laddar teams..." : "Välj team..."}
                    </option>
                    {teamSelectGroups.map((group) => (
                        <optgroup key={group.label} label={group.label}>
                            {group.teams.map((team) => (
                                <option key={team.id} value={team.id}>
                                    {team.name}
                                </option>
                            ))}
                        </optgroup>
                    ))}
                </select>
                {errors.teamId && (
                    <span id="teamId-error" role="alert" className="error-message">
                        {errors.teamId}
                    </span>
                )}
            </div>

            <div className="form-group">
                <label htmlFor="name">
                    Namn <span aria-label="obligatoriskt">*</span>
                </label>
                <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    maxLength={200}
                    aria-required="true"
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? "name-error" : undefined}
                    className={errors.name ? "error-field" : ""}
                />
                {errors.name && (
                    <span id="name-error" role="alert" className="error-message">
                        {errors.name}
                    </span>
                )}
            </div>

            <div className="form-group">
                <label htmlFor="description">Beskrivning</label>
                <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    maxLength={1000}
                    rows={4}
                    aria-invalid={!!errors.description}
                    aria-describedby={errors.description ? "description-error" : undefined}
                    className={errors.description ? "error-field" : ""}
                />
                {errors.description && (
                    <span id="description-error" role="alert" className="error-message">
                        {errors.description}
                    </span>
                )}
            </div>

            <div className="form-group">
                <label htmlFor="startDate">Startdatum</label>
                <input
                    id="startDate"
                    name="startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={handleChange}
                    aria-invalid={!!errors.startDate}
                    aria-describedby={errors.startDate ? "startDate-error" : undefined}
                    className={errors.startDate ? "error-field" : ""}
                />
                {errors.startDate && (
                    <span id="startDate-error" role="alert" className="error-message">
                        {errors.startDate}
                    </span>
                )}
            </div>

            <div className="form-group">
                <label htmlFor="endDate">Slutdatum</label>
                <input
                    id="endDate"
                    name="endDate"
                    type="date"
                    value={formData.endDate}
                    onChange={handleChange}
                    aria-invalid={!!errors.endDate}
                    aria-describedby={errors.endDate ? "endDate-error" : undefined}
                    className={errors.endDate ? "error-field" : ""}
                />
                {errors.endDate && (
                    <span id="endDate-error" role="alert" className="error-message">
                        {errors.endDate}
                    </span>
                )}
            </div>

            <div className="form-actions">
                <button
                    type="button"
                    className="button button--secondary"
                    onClick={onCancel}
                    disabled={isLoading}
                >
                    Avbryt
                </button>
                <button
                    type="submit"
                    className="button button--primary"
                    disabled={isLoading}
                >
                    {isLoading ? "Sparar..." : isEditMode ? "Spara" : "Skapa"}
                </button>
            </div>
        </form>
    );
};